import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDeleteIamGroup, useGroupDetails, useGroupsAndUsers, useRemoveUserFromGroup } from '../../hooks/use-iam';
import { useTranslation } from 'react-i18next';
import DataTable from 'components/blocks/data-table/data-table';
import { Button } from 'components/ui/button';
import { ArrowLeft, Plus } from 'lucide-react';
import { createGroupTableColumns } from './iam-group-table-columns';
import { Dialog, DialogTrigger } from '@radix-ui/react-dialog';
import { AddMembersToGroupModal } from './add-member-to-group-modal';
import { createGroupDetailsTableColumns } from './iam-group-details-table-columns';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { RenameGroupModal } from './edit-group-modal';
import ConfigurationButton from 'components/configuration/ConfigurationButton';

export interface GroupData {
    id: string;
    groupName: string;
    createdBy: string;
    createdDate: string;
    users: [];
}

interface GroupDataTableProps {
    isReload?: boolean;
}

interface PaginationState {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
}

export default function GroupDataTable({ isReload }: GroupDataTableProps) {
    const { t } = useTranslation();

    const [searchParams, setSearchParams] = useSearchParams();
    const { mutateAsync: getGroupsAndUsers } = useGroupsAndUsers();
    const { mutateAsync: getGroupDetails } = useGroupDetails();
    const { mutateAsync: removeUserFromGroup } = useRemoveUserFromGroup();
    const { mutateAsync: deleteIamGroup } = useDeleteIamGroup();

    const [data, setData] = useState<GroupData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
    const [groupDetails, setGroupDetails] = useState<any>(null);
    const [isAddMemberModalOpen, setAddMemberModalOpen] = useState(false);
    const [openMemberDeleteConfirmationModal, setOpenMemberDeleteConfirmationModal] = useState(false);
    const [userToRemove, setUserToRemove] = useState<any>(null);

    const [openGroupDeleteConfirmationModal, setOpenGroupDeleteConfirmationModal] = useState(false);
    const [openGroupRenameConfirmationModal, setOpenGroupRenameConfirmationModal] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<GroupData | null>(null);
    const [groupToRename, setGroupToRename] = useState<GroupData | null>(null);

    const [paginationState, setPaginationState] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
        totalCount: 0,
    });

    const [detailsPaginationState, setDetailsPaginationState] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
        totalCount: 0,
    });

    const columns = useMemo(
        () =>
            createGroupTableColumns({
                t,
                onDeleteGroup: (group: any) => {
                    setGroupToDelete(group);
                    setOpenGroupDeleteConfirmationModal(true);
                },
                onViewDetails: async (group: any): Promise<void> => {
                    await handleViewDetails(group.original);
                },
                onEditGroup: async (group: any): Promise<void> => {
                    setGroupToRename(group.original);
                    setOpenGroupRenameConfirmationModal(true);
                },
            }) as any,
        [t]
    );

    const groupDetailTablleColumn = createGroupDetailsTableColumns({
        t,
        onDelete: async (user: any): Promise<void> => {
            setUserToRemove(user);
            setOpenMemberDeleteConfirmationModal(true);
        },
    });

    const removeMemberFromGroup = async () => {
        if (!userToRemove) return;

        const payload = {
            userIds: [userToRemove.itemId],
            groupId: selectedGroup?.id || '',
        };

        try {
            const response: any = await removeUserFromGroup(payload);
            if (response) {
                getGroupDetailsById();
            }
        } catch (error) {
            console.error('Error removing user from group:', error);
        } finally {
            setOpenMemberDeleteConfirmationModal(false);
            setUserToRemove(null);
        }
    };

    const confirmDeleteGroup = async () => {
        if (!groupToDelete) return;

        const userIds = groupToDelete.users?.map((u: any) => u.itemId) || [];
        const payload = {
            groupId: groupToDelete.id,
            userIds,
        };

        try {
            await deleteIamGroup(payload);
            fetchGroups();
            setSelectedGroup(null);
        } catch (error) {
            console.error('Error deleting group:', error);
        } finally {
            setOpenGroupDeleteConfirmationModal(false);
            setGroupToDelete(null);
        }
    };

    const groupId = searchParams.get('group');

    const fetchGroups = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const payload = {
                page: paginationState.pageIndex,
                pageSize: paginationState.pageSize,
                userPage: '0',
                UserPageSize: '10',
            };
            const response: any = await getGroupsAndUsers(payload);
            setData(response.groupsDetails || []);
            setPaginationState((prev) => ({
                ...prev,
                totalCount: response.totalCount ?? response.groupsDetails?.length ?? 0,
            }));
        } catch (err: any) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const getGroupDetailsById = async () => {
        if (!selectedGroup?.id) return;

        setIsLoading(true);
        try {
            const payload = {
                groupId: selectedGroup.id,
                page: detailsPaginationState.pageIndex,
                pageSize: detailsPaginationState.pageSize,
            };
            const response: any = await getGroupDetails(payload);
            setGroupDetails(response);
            setDetailsPaginationState((prev) => ({
                ...prev,
                totalCount: response.totalCount ?? response.users?.length ?? 0,
            }));
        } catch (error) {
            console.error('Error fetching group details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedGroup) {
            getGroupDetailsById();
        }
    }, [selectedGroup?.id, detailsPaginationState.pageIndex, detailsPaginationState.pageSize]);

    useEffect(() => {
        if (groupDetails?.totalCount !== undefined) {
            setDetailsPaginationState((prev) => ({
                ...prev,
                totalCount: groupDetails.totalCount,
            }));
        }
    }, [groupDetails?.totalCount]);

    useEffect(() => {
        if (groupId && data.length > 0) {
            const foundGroup = data.find(group => group.id === groupId);
            setSelectedGroup(foundGroup || null);
        } else {
            setSelectedGroup(null);
        }
    }, [groupId, data, isReload]);

    useEffect(() => {
        fetchGroups();
    }, [paginationState.pageIndex, paginationState.pageSize, isReload]);

    const handleViewDetails = (row: GroupData) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('group', row.id);
        setSearchParams(newSearchParams);
        setSelectedGroup(row);
        setDetailsPaginationState({ pageIndex: 0, pageSize: 10, totalCount: 0 });
    };

    const handleBack = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('group');
        setSearchParams(newSearchParams);
        setSelectedGroup(null);
        setGroupDetails(null);
        setDetailsPaginationState({ pageIndex: 0, pageSize: 10, totalCount: 0 });
    };

    const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
        setPaginationState((prev) => ({ ...prev, ...newPagination }));
    };

    const handleDetailsPaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
        setDetailsPaginationState((prev) => ({ ...prev, ...newPagination }));
    };

    const reloadData = () => {
        fetchGroups();
        getGroupDetailsById();
    };

    const renderFilterToolbar = () => {
        if (selectedGroup) {
            return (
                <div className="flex items-center justify-between p-2">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" onClick={handleBack} size="sm">
                            <ArrowLeft />
                        </Button>
                        <h3 className="text-base font-medium">{selectedGroup.groupName}</h3>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <Dialog open={isAddMemberModalOpen} onOpenChange={setAddMemberModalOpen}>
                            <DialogTrigger asChild>
                                <ConfigurationButton>
                                    <Plus size={20} className="sm:mr-2" />
                                    <span className="hidden sm:inline">{t('ADD_MEMBER')}</span>
                                </ConfigurationButton>
                            </DialogTrigger>
                            <AddMembersToGroupModal reloadData={reloadData} selectedGroup={selectedGroup} isOpen={isAddMemberModalOpen} onClose={() => setAddMemberModalOpen(false)} />
                        </Dialog>
                    </div>
                </div>
            );
        }
    };

    const renderExpandedContent = (row: GroupData) => (
        <div className="p-4 text-sm text-muted-foreground">
            <p><strong>{t('CREATED_BY')}:</strong> {row.createdBy || '-'}</p>
            <p><strong>{t('CREATED_AT')}:</strong> {row.createdDate ? new Date(row.createdDate).toLocaleString() : '-'}</p>
        </div>
    );

    if (selectedGroup) {
        return (
            <div className="space-y-4">
                <DataTable
                    data={groupDetails?.users || []}
                    columns={groupDetailTablleColumn}
                    isLoading={isLoading}
                    toolbar={renderFilterToolbar}
                    pagination={detailsPaginationState}
                    onPaginationChange={handleDetailsPaginationChange}
                    manualPagination
                    expandable
                />

                <ConfirmationModal
                    open={openMemberDeleteConfirmationModal}
                    onOpenChange={setOpenMemberDeleteConfirmationModal}
                    title={t('REMOVE_USER_FROM_GROUP')}
                    description={`${t('ARE_YOU_SURE_REMOVE')} ${userToRemove?.firstName || ''} ${userToRemove?.lastName || ''} ${userToRemove?.email ? `(${userToRemove.email})` : ''} ${t('FROM_GROUP')} "${selectedGroup.groupName}"?`}
                    onConfirm={removeMemberFromGroup}
                    confirmText={t('CONFIRM')}
                    cancelText={t('CANCEL')}
                />
            </div>
        );
    }

    return (
        <div>
            <DataTable
                data={data}
                columns={columns}
                onRowClick={handleViewDetails}
                isLoading={isLoading}
                error={error}
                toolbar={renderFilterToolbar}
                pagination={paginationState}
                onPaginationChange={handlePaginationChange}
                manualPagination
                expandedContent={renderExpandedContent}
                mobileColumns={['groupName']}
                expandable={false}
            />

            <ConfirmationModal
                open={openGroupDeleteConfirmationModal}
                onOpenChange={setOpenGroupDeleteConfirmationModal}
                title={t('DELETE_GROUP')}
                description={`${t('ARE_YOU_SURE_DELETE_GROUP')} "${groupToDelete?.groupName}"?`}
                onConfirm={confirmDeleteGroup}
                confirmText={t('CONFIRM')}
                cancelText={t('CANCEL')}
                confirmVariant="delete"
            />

            <RenameGroupModal reloadData={reloadData} group={groupToRename} isOpen={openGroupRenameConfirmationModal} onClose={() => setOpenGroupRenameConfirmationModal(false)} />
        </div>
    );
}
